import { z } from "zod";

import { FormResponse, formSchema } from "~/api/forms";

export type RecordSearchFilters = {
  clientId: number;
  page: number;
  searchterm?: string;
  size: number;
  sortattr: string;
  sortdir: "ASC" | "DESC";
};

export const recordSearchRecordResponseSchema = z.object({
  id: z.number(),
  clientId: z.number(),
  accessLevel: z.string(),
  type: z.string(),
  status: z.enum(["DRAFT", "SIGNED"]).optional(),
  description: z.string().optional(),
  clientCreated: z.string().datetime(),
  created: z.string().datetime(),
  updated: z.string().datetime().optional(),
  courseId: z.number().optional(),
  user: z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string().optional(),
  }),
  creator: z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string().optional(),
  }),
  restricted: z.boolean(),
});

const sessionBaseSchema = z.object({
  id: z.number(),
  clientId: z.number(),
  accessLevel: z.string().optional(),
  type: z.string(),
  description: z.string().optional(),
  clientCreated: z.string().datetime(),
  created: z.string().datetime(),
  updated: z.string().datetime().optional(),
  attended: z.boolean(),
  children: z.array(recordSearchRecordResponseSchema),
});

const groupSessionRecordsResponseSchema = sessionBaseSchema.extend({
  groupId: z.number(),
  groupName: z.string(),
  sessionId: z.number(),
});

const whanauSessionRecordsResponseSchema = sessionBaseSchema.extend({
  whanauId: z.number(),
  whanauSessionId: z.number(),
});

export const recordSearchResponseSchema = z.object({
  searchterm: z.string(),
  page: z.number(),
  size: z.number(),
  results: z.array(
    z.union([
      recordSearchRecordResponseSchema,
      groupSessionRecordsResponseSchema,
      whanauSessionRecordsResponseSchema,
    ])
  ),
});

export type RecordSearchResponse = z.infer<typeof recordSearchResponseSchema>;

const recordTypeSchema = z.enum(["CONSULTATION", "WHANAU_SESSION_RECORD", "SESSION_RECORD"]);

export type RecordType = z.infer<typeof recordTypeSchema>;

const recordContentSchema = z
  .object({
    model: z.record(z.string(), z.unknown()).optional(),
    placeholders: z.record(z.string(), z.unknown()).optional(),
  })
  .optional();

export const recordQuestionSchema = z.object({
  id: z.number(),
  name: z.string().optional(),
  content: z.preprocess(parseContent, recordContentSchema),
  form: z.preprocess(parseForm, formSchema),
});

export const createRecordRequestSchema = z.object({
  patientId: z.number().optional().nullable(),
  type: recordTypeSchema,
  courseId: z.number().optional().nullable(),
  form: z
    .object({
      id: z.number(),
    })
    .optional(),
  teamId: z.number(),
  modalityId: z.number().optional(),
  whanauSessionId: z.number().optional(),
  sessionId: z.number().optional(),
  status: z.string(),
  questions: z.array(z.unknown()).optional(),
  content: z.record(z.string(), z.unknown()),
});

export const stringifiedCreateRecordRequestSchema = createRecordRequestSchema.extend({
  content: z.string(),
});

export type StringifiedCreateRecordRequest = z.infer<typeof stringifiedCreateRecordRequestSchema>;

export type CreateRecordRequest = z.infer<typeof createRecordRequestSchema>;

const recordUpdateQuestionSchema = z.object({
  id: z.number().optional(),
  name: z.string().optional(),
  content: recordContentSchema,
  form: z.object({
    id: z.number(),
  }),
});

export const updateRecordRequestSchema = z.object({
  id: z.number(),
  patientId: z.number().optional().nullable(),
  type: recordTypeSchema,
  courseId: z.number().optional().nullable(),
  clientCreated: z.string().datetime(),
  description: z.string().optional(),
  form: z.object({
    id: z.number(),
  }),
  teamId: z.number(),
  modalityId: z.number().optional(),
  whanauSessionId: z.number().optional(),
  sessionId: z.number().optional(),
  status: z.enum(["DRAFT", "SIGNED"]),
  questions: z.array(recordUpdateQuestionSchema),
  content: recordContentSchema,
  version: z.number(),
  sid: z.string(),
});

export const parsedUpdateRecordQuestionRequestSchema = recordUpdateQuestionSchema.extend({
  content: z.preprocess(stringifyContent, z.string()),
});

export const parsedUpdateRecordRequestSchema = updateRecordRequestSchema.extend({
  questions: z.array(parsedUpdateRecordQuestionRequestSchema).optional(),
  content: z.preprocess(stringifyContent, z.string()),
});

export type ParsedUpdateRecordRequest = z.infer<typeof parsedUpdateRecordRequestSchema>;

export type UpdateRecordRequest = z.infer<typeof updateRecordRequestSchema>;

const baseRecordResponse = z.object({
  clientCreated: z.string().datetime(),
  creator: z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string().optional(),
  }),
  description: z.string().optional(),
  id: z.number(),
  patientId: z.number().optional(),
  teamId: z.number().optional(),
  teamName: z.string().optional(),
  type: recordTypeSchema,
  user: z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string().optional(),
  }),
});

export const restrictedAccessRecordResponseSchema = baseRecordResponse.extend({
  restricted: z.literal(true),
});

export type RestrictedAccessRecordResponse = z.infer<typeof restrictedAccessRecordResponseSchema>;

export const fullAccessRecordResponseSchema = baseRecordResponse.extend({
  content: z.preprocess(parseContent, recordContentSchema),
  courseId: z.number(),
  created: z.string().datetime(),
  duration: z.string().optional(),
  form: z.preprocess(parseForm, formSchema),
  links: z
    .object({
      reassign: z.string().optional(),
    })
    .optional(),
  modalityId: z.number(),
  questions: z.array(recordQuestionSchema),
  restricted: z.literal(false),
  sid: z.string(),
  status: z.enum(["DRAFT", "SIGNED"]),
  updated: z.string().datetime().optional(),
  version: z.number(),
  whanauSessionId: z.number().optional(),
  sessionId: z.number().optional(),
});

export type FullAccessRecordResponse = z.infer<typeof fullAccessRecordResponseSchema>;

export const recordResponseSchema = z.union([
  restrictedAccessRecordResponseSchema,
  fullAccessRecordResponseSchema,
]);

export type RecordResponse = z.infer<typeof recordResponseSchema>;

function parseContent(content: unknown) {
  if (typeof content === "string") {
    try {
      const parsedContent = JSON.parse(content);
      if (!parsedContent.model || Object.keys(parsedContent.model).length === 0) {
        parsedContent.model = undefined;
      }
      if (!parsedContent.placeholders || Object.keys(parsedContent.placeholders).length === 0) {
        parsedContent.placeholders = undefined;
      }
      return parsedContent;
    } catch {
      return {
        model: undefined,
        placeholders: undefined,
      };
    }
  }
}

function parseForm(val: unknown) {
  const unparsedForm = val as FormResponse;
  const parsedJson = JSON.parse(unparsedForm.json);
  return {
    ...unparsedForm,
    json: parsedJson,
  };
}

function stringifyContent(content: unknown) {
  if (content && typeof content === "object") {
    return JSON.stringify(content);
  }
  return JSON.stringify({
    model: undefined,
    placeholders: undefined,
  });
}

const recordHistoryEntrySchema = z.object({
  id: z.number(),
  status: z.string(),
  created: z.string().datetime(),
  worker: z.string(),
  workerId: z.number(),
});
export type RecordHistoryEntry = z.infer<typeof recordHistoryEntrySchema>;

export const recordHistoryResponseSchema = z.array(recordHistoryEntrySchema);

export const createRecordAddendumRequestSchema = z.object({
  consultationId: z.number(),
  title: z.string(),
  description: z.string().optional(),
});

export type CreateRecordAddendumRequest = z.infer<typeof createRecordAddendumRequestSchema>;

export const recordAddendumSchema = createRecordAddendumRequestSchema.extend({
  id: z.number(),
  created: z.string().datetime(),
});

export type RecordAddendum = z.infer<typeof recordAddendumSchema>;

export const recordAddendaResponseSchema = z.array(recordAddendumSchema);

export type RecordAddendaResponse = z.infer<typeof recordAddendaResponseSchema>;
